import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Notify } from "@/components/ui/notify";
import { JustifyEventDialog } from "@/features/machine-sidebar/JustifyEventDialog";
import { QtyForm } from "./forms/QtyForm";
import { LossForm } from "./forms/LossForm";
import { PauseObsForm } from "./forms/PauseObsForm";
import { OperationalEntryButtons } from "./OperationalEntryButtons";
import { useOperationalEntryState } from "./state";
import type { ActiveRun } from "./types";

interface Props {
  lineId: string;
  activeRun: ActiveRun | null | undefined;
  triggerLabel?: string;
  variant?: "disclosure" | "buttons";
}

export function OperationalEntryDisclosure({
  lineId,
  activeRun,
  triggerLabel = "Registrar dado operacional",
  variant = "disclosure",
}: Props) {
  if (!activeRun) {
    return (
      <p className="mt-3 rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground">
        Inicie uma produção pela barra de comandos acima para registrar dados operacionais.
      </p>
    );
  }

  if (variant === "buttons") {
    return (
      <div className="mt-3">
        <OperationalEntryButtons lineId={lineId} activeRun={activeRun} />
      </div>
    );
  }

  return <DisclosureVariant lineId={lineId} activeRun={activeRun} triggerLabel={triggerLabel} />;
}

interface DisclosureVariantProps {
  lineId: string;
  activeRun: ActiveRun;
  triggerLabel: string;
}

function DisclosureVariant({ lineId, activeRun, triggerLabel }: DisclosureVariantProps) {
  const [expanded, setExpanded] = useState(false);
  const {
    openAction,
    setOpenAction,
    closeAction,
    justifyEventId,
    setJustifyEventId,
    pendingEvents,
    isPaused,
    isError,
    error,
    refetchEvents,
  } = useOperationalEntryState(lineId, activeRun);

  return (
    <div className="mt-3 space-y-2">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-1.5 rounded-md border bg-muted/20 px-3 py-2 text-left text-xs font-medium hover:bg-muted/40"
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" />
        )}
        {triggerLabel}
      </button>

      {expanded ? (
        <div className="space-y-2 rounded-md border bg-muted/10 p-3">
          {openAction !== "quantity" ? (
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start"
              onClick={() => setOpenAction("quantity")}
              disabled={isPaused}
            >
              Registrar quantidade produzida
            </Button>
          ) : (
            <QtyForm lineId={lineId} onDone={closeAction} />
          )}

          {openAction !== "loss" ? (
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start"
              onClick={() => setOpenAction("loss")}
            >
              Registrar perda
            </Button>
          ) : (
            <LossForm lineId={lineId} onDone={closeAction} />
          )}

          {isError ? (
            <Notify.QueryError
              variant="inline"
              error={error}
              onRetry={() => void refetchEvents()}
              title="Não foi possível carregar eventos pendentes"
            />
          ) : pendingEvents.length > 0 ? (
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start"
              onClick={() => setJustifyEventId(pendingEvents[0]!.id)}
            >
              Justificar evento ({pendingEvents.length} pendente
              {pendingEvents.length === 1 ? "" : "s"})
            </Button>
          ) : null}

          {!isPaused ? (
            openAction !== "pause-obs" ? (
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => setOpenAction("pause-obs")}
              >
                Pausar com observação
              </Button>
            ) : (
              <PauseObsForm lineId={lineId} runId={activeRun.id} onDone={closeAction} />
            )
          ) : (
            <p className="rounded border bg-muted/20 p-2 text-xs text-muted-foreground">
              Produção pausada. Use a barra de comandos acima para retomar ou finalizar.
            </p>
          )}
        </div>
      ) : null}

      <JustifyEventDialog
        eventId={justifyEventId}
        open={!!justifyEventId}
        onClose={() => setJustifyEventId(null)}
      />
    </div>
  );
}
